import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Typography,
} from '@mui/material'
import ConflictWarning from './ConflictWarning'

const LINES = ['Line 1', 'Line 2', 'Line 3', 'Line 4']
const MONTHS = [
  'Apr 2026', 'May 2026', 'Jun 2026', 'Jul 2026',
  'Aug 2026', 'Sep 2026', 'Oct 2026', 'Nov 2026',
  'Dec 2026', 'Jan 2027', 'Feb 2027', 'Mar 2027',
]
const DEMO_ITEMS = [
  'MG CHILI BNS 12/15', 'HV CHILI BNS TP 12/15.5',
  'FINEST CHILI BNS TP', 'MG TX MX CHILI BN 12/15',
  'CV CHILI BNS 12/15.5',
]
const DEMO_BRITES = ['12221', '12321', '12421']

function SequencingFields({ form, onChange }) {
  return (
    <>
      <FormControl fullWidth>
        <InputLabel>Lines</InputLabel>
        <Select
          multiple
          value={form.lines || []}
          onChange={(e) => onChange('lines', e.target.value)}
          input={<OutlinedInput label="Lines" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((v) => <Chip key={v} label={v} size="small" />)}
            </Box>
          )}
        >
          {LINES.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField
        label="Sort Order"
        value={form.sortOrder || ''}
        onChange={(e) => onChange('sortOrder', e.target.value)}
        placeholder="e.g., light-to-dark"
        fullWidth
      />
      <TextField
        label="Group By"
        value={form.groupBy || ''}
        onChange={(e) => onChange('groupBy', e.target.value)}
        placeholder="e.g., Brite Code"
        fullWidth
      />
    </>
  )
}

function MinQuantityFields({ form, onChange }) {
  return (
    <>
      <TextField
        label="Scope"
        value={form.scope || ''}
        onChange={(e) => onChange('scope', e.target.value)}
        placeholder="e.g., All Brite Codes, Specific Items..."
        fullWidth
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Threshold Value"
          type="number"
          value={form.threshold || ''}
          onChange={(e) => onChange('threshold', e.target.value)}
          fullWidth
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Unit</InputLabel>
          <Select
            value={form.unit || 'cases'}
            label="Unit"
            onChange={(e) => onChange('unit', e.target.value)}
          >
            <MenuItem value="cases">Cases</MenuItem>
            <MenuItem value="cans">Cans</MenuItem>
            <MenuItem value="hours">Hours</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TextField
        label="Group By"
        value={form.groupBy || ''}
        onChange={(e) => onChange('groupBy', e.target.value)}
        placeholder="e.g., Brite Code"
        fullWidth
      />
    </>
  )
}

function CapReservationFields({ form, onChange }) {
  return (
    <>
      <FormControl fullWidth>
        <InputLabel>Line</InputLabel>
        <Select
          value={form.line || ''}
          label="Line"
          onChange={(e) => onChange('line', e.target.value)}
        >
          {LINES.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Start Month</InputLabel>
          <Select
            value={form.startMonth || ''}
            label="Start Month"
            onChange={(e) => onChange('startMonth', e.target.value)}
          >
            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>End Month</InputLabel>
          <Select
            value={form.endMonth || ''}
            label="End Month"
            onChange={(e) => onChange('endMonth', e.target.value)}
          >
            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <TextField
        label="Reserved Hours"
        type="number"
        value={form.reservedHours || ''}
        onChange={(e) => onChange('reservedHours', e.target.value)}
        fullWidth
      />
    </>
  )
}

function ItemPairingFields({ form, onChange }) {
  return (
    <>
      <FormControl fullWidth>
        <InputLabel>Item A</InputLabel>
        <Select
          value={form.itemA || ''}
          label="Item A"
          onChange={(e) => onChange('itemA', e.target.value)}
        >
          {DEMO_ITEMS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Item B</InputLabel>
        <Select
          value={form.itemB || ''}
          label="Item B"
          onChange={(e) => onChange('itemB', e.target.value)}
        >
          {DEMO_ITEMS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
        </Select>
      </FormControl>
    </>
  )
}

function ExclusionFields({ form, onChange }) {
  return (
    <>
      <FormControl fullWidth>
        <InputLabel>Item / Brite A</InputLabel>
        <Select
          value={form.excludeA || ''}
          label="Item / Brite A"
          onChange={(e) => onChange('excludeA', e.target.value)}
        >
          {[...DEMO_ITEMS, ...DEMO_BRITES].map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Item / Brite B</InputLabel>
        <Select
          value={form.excludeB || ''}
          label="Item / Brite B"
          onChange={(e) => onChange('excludeB', e.target.value)}
        >
          {[...DEMO_ITEMS, ...DEMO_BRITES].map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  )
}

const TYPE_FIELDS = {
  sequencing: SequencingFields,
  minQuantity: MinQuantityFields,
  capReservation: CapReservationFields,
  itemPairing: ItemPairingFields,
  exclusion: ExclusionFields,
}

export default function RuleForm({ open, onClose, onSave, ruleType, editRule }) {
  const [form, setForm] = useState({})
  const [conflicts, setConflicts] = useState([])

  useEffect(() => {
    if (editRule) {
      setForm(editRule)
    } else {
      setForm({ name: '', description: '' })
    }
    setConflicts([])
  }, [editRule, open])

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))

    if (field === 'line' && value === 'Line 1' && ruleType === 'capReservation') {
      setConflicts(['Line 1 already has 24h maintenance in Jul 2026 — reserved hours may overlap.'])
    } else {
      setConflicts([])
    }
  }

  const handleSave = () => {
    onSave?.({ ...form, type: ruleType })
    onClose()
  }

  const FieldComponent = TYPE_FIELDS[ruleType]
  const typeLabel = {
    sequencing: 'Sequencing',
    minQuantity: 'Min Quantity',
    capReservation: 'Capacity Reservation',
    itemPairing: 'Item Pairing',
    exclusion: 'Exclusion',
  }[ruleType] || ruleType

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editRule ? 'Edit' : 'Add'} {typeLabel} Rule</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <ConflictWarning conflicts={conflicts} />

        <TextField
          label="Rule Name"
          value={form.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          fullWidth
          sx={{ mt: 1 }}
        />
        <TextField
          label="Description"
          value={form.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          multiline
          rows={2}
          fullWidth
        />

        {FieldComponent && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              {typeLabel} Settings
            </Typography>
            <FieldComponent form={form} onChange={handleChange} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form.name}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
